/**
 * Parts mass-import modal. Opens from the Parts List page.
 *
 * Flow:
 *   1. User picks a CSV file (or drops it).
 *   2. Client parses it with xlsx.read (same dep the Optimizer export
 *      uses; no new dep for CSV).
 *   3. Show a small preview of the first 10 rows so the user can
 *      confirm the file is right before we POST.
 *   4. On confirm, POST the parsed rows to /api/catalog/import (server
 *      does upsert by (vendor, sku)).
 *   5. Show the result summary: X created / Y updated / Z errors, with
 *      an expandable list of per-row error messages.
 *
 * The template header names must match what the server accepts:
 *   sku, name, vendor, oem_no, category, unit_price, lead_time_days
 *
 * Vendor callers don't need to fill the vendor column; platform callers
 * do (server rejects otherwise with "vendor is required for platform").
 */
import React, { useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import {
  type CatalogImportResponse,
  type CatalogImportRow,
  importCatalog,
} from "../../services/catalogApi";

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

const PREVIEW_ROWS = 10;
const MAX_ROWS = 2000;

const RECOGNIZED_HEADERS: Record<string, keyof CatalogImportRow> = {
  sku: "sku",
  name: "name",
  vendor: "vendor",
  oem_no: "oem_no",
  "oem no": "oem_no",
  "oem number": "oem_no",
  category: "category",
  unit_price: "unit_price",
  "unit price": "unit_price",
  price: "unit_price",
  lead_time_days: "lead_time_days",
  "lead time (days)": "lead_time_days",
  "lead time days": "lead_time_days",
  "lead time": "lead_time_days",
};

/** Coerce a spreadsheet cell (which xlsx returns as string|number|boolean)
 *  into an import-row value. Blank / undefined → null so the server sees
 *  "omitted" instead of "empty string". */
function coerceCell(raw: unknown): string | number | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number") return raw;
  if (typeof raw === "boolean") return raw ? "true" : "false";
  const s = String(raw).trim();
  return s === "" ? null : s;
}

function parseCsvFile(text: string): CatalogImportRow[] {
  // xlsx handles quoted fields, embedded newlines, and CRLF correctly.
  const wb = XLSX.read(text, { type: "string" });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(sheet, {
    defval: null,
    raw: false,
  });
  return rows.map((r) => {
    const bag: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(r)) {
      const field = RECOGNIZED_HEADERS[key.trim().toLowerCase()];
      if (!field) continue;
      const v = coerceCell(value);
      if (field === "unit_price" || field === "lead_time_days") {
        if (v === null) {
          bag[field] = null;
        } else {
          const n = Number(v);
          bag[field] = Number.isFinite(n) ? n : v;
        }
      } else {
        bag[field] = v ?? "";
      }
    }
    // Widen through unknown; the server rejects invalid shapes with a
    // clear per-row error, so a bad row here surfaces cleanly to the UI.
    return {
      sku: String(bag.sku ?? ""),
      name: String(bag.name ?? ""),
      vendor: bag.vendor as string | null | undefined,
      oem_no: bag.oem_no as string | null | undefined,
      category: bag.category as string | null | undefined,
      unit_price: bag.unit_price as number | null | undefined,
      lead_time_days: bag.lead_time_days as number | null | undefined,
    } satisfies CatalogImportRow;
  });
}

export const PartsImportModal: React.FC<Props> = ({
  open,
  onClose,
  onImported,
}) => {
  const { token, role } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<CatalogImportRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CatalogImportResponse | null>(null);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const previewRows = useMemo(() => rows.slice(0, PREVIEW_ROWS), [rows]);
  const isPlatform = role === "platform";

  const reset = (): void => {
    setFile(null);
    setRows([]);
    setParseError(null);
    setResult(null);
    setShowAllErrors(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onFile = async (f: File): Promise<void> => {
    setParseError(null);
    setResult(null);
    setFile(f);
    try {
      const text = await f.text();
      const parsed = parseCsvFile(text);
      if (parsed.length === 0) {
        setRows([]);
        setParseError("File has no data rows.");
        return;
      }
      if (parsed.length > MAX_ROWS) {
        setRows([]);
        setParseError(`Too many rows (${parsed.length}). Max ${MAX_ROWS} per import.`);
        return;
      }
      setRows(parsed);
    } catch (e) {
      setRows([]);
      setParseError(e instanceof Error ? e.message : "Could not parse file");
    }
  };

  const onSubmit = async (): Promise<void> => {
    if (!token || rows.length === 0) return;
    setSubmitting(true);
    try {
      const r = await importCatalog(token, rows);
      setResult(r);
      if (r.created + r.updated > 0) onImported();
    } catch (e) {
      setParseError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const onDialogClose = (): void => {
    reset();
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onDialogClose}
    >
      <div
        className="bg-white rounded-md shadow-lg max-w-3xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-4 py-3 border-b border-secondary-200 flex items-center justify-between">
          <h2 className="text-base font-semibold">Import parts from CSV</h2>
          <button
            type="button"
            onClick={onDialogClose}
            className="text-secondary-500 hover:text-secondary-800 text-lg leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="p-4 overflow-y-auto flex-1 space-y-3 text-sm">
          {/* Instructions */}
          {!result && (
            <div className="text-xs text-secondary-600 space-y-1">
              <p>
                Upload a CSV matching the template. Columns: <code>sku</code>,{" "}
                <code>name</code>
                {isPlatform && (
                  <>
                    , <code>vendor</code> <em>(required for platform)</em>
                  </>
                )}
                , <code>oem_no</code>, <code>category</code>,{" "}
                <code>unit_price</code>, <code>lead_time_days</code>.
              </p>
              <p>
                Rows with a SKU that already exists for the vendor are{" "}
                <strong>updated in place</strong>; new SKUs are created. Blank
                optional fields keep the server default (Category / OEM / Price
                stay empty; Lead time defaults to 0).
              </p>
            </div>
          )}

          {/* File picker */}
          {!result && (
            <div className="border border-dashed border-secondary-300 rounded-md p-3">
              <label className="text-xs text-secondary-700">
                <div className="mb-1">Select a CSV file</div>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void onFile(f);
                  }}
                  className="text-xs"
                />
              </label>
              {file && (
                <div className="mt-2 text-[11px] text-secondary-500">
                  {file.name} — {(file.size / 1024).toFixed(1)} KB — {rows.length} row
                  {rows.length === 1 ? "" : "s"} detected
                </div>
              )}
            </div>
          )}

          {parseError && (
            <div className="text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded text-xs">
              {parseError}
            </div>
          )}

          {/* Preview */}
          {!result && rows.length > 0 && (
            <div>
              <div className="text-xs text-secondary-600 mb-1">
                Preview (first {Math.min(PREVIEW_ROWS, rows.length)} of{" "}
                {rows.length}):
              </div>
              <div className="border border-secondary-200 rounded overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-secondary-50 text-secondary-600 uppercase">
                    <tr>
                      <th className="px-2 py-1 text-left">SKU</th>
                      <th className="px-2 py-1 text-left">Name</th>
                      {isPlatform && (
                        <th className="px-2 py-1 text-left">Vendor</th>
                      )}
                      <th className="px-2 py-1 text-left">OEM</th>
                      <th className="px-2 py-1 text-left">Category</th>
                      <th className="px-2 py-1 text-right">Price</th>
                      <th className="px-2 py-1 text-right">Lead time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100">
                    {previewRows.map((r, i) => (
                      <tr key={i}>
                        <td className="px-2 py-1 font-mono">{r.sku}</td>
                        <td className="px-2 py-1">{r.name}</td>
                        {isPlatform && (
                          <td className="px-2 py-1">{r.vendor ?? ""}</td>
                        )}
                        <td className="px-2 py-1 font-mono">{r.oem_no ?? ""}</td>
                        <td className="px-2 py-1">{r.category ?? ""}</td>
                        <td className="px-2 py-1 text-right">
                          {r.unit_price ?? ""}
                        </td>
                        <td className="px-2 py-1 text-right">
                          {r.lead_time_days ?? ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-success-50 border border-success-200 rounded p-2">
                  <div className="text-secondary-600">Created</div>
                  <div className="text-lg font-semibold">{result.created}</div>
                </div>
                <div className="bg-primary-50 border border-primary-200 rounded p-2">
                  <div className="text-secondary-600">Updated</div>
                  <div className="text-lg font-semibold">{result.updated}</div>
                </div>
                <div
                  className={
                    "border rounded p-2 " +
                    (result.errors > 0
                      ? "bg-error-50 border-error-200"
                      : "bg-secondary-50 border-secondary-200")
                  }
                >
                  <div className="text-secondary-600">Errors</div>
                  <div className="text-lg font-semibold">{result.errors}</div>
                </div>
              </div>

              {result.errors > 0 && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowAllErrors((v) => !v)}
                    className="text-xs text-primary-600 hover:underline"
                  >
                    {showAllErrors ? "Hide" : "Show"} errored rows
                  </button>
                  {showAllErrors && (
                    <div className="mt-1 border border-error-200 rounded p-2 max-h-52 overflow-y-auto text-[11px] space-y-1">
                      {result.results
                        .filter((r) => r.action === "error")
                        .map((r) => (
                          <div key={r.row}>
                            Row {r.row}
                            {r.sku && (
                              <span className="font-mono"> · {r.sku}</span>
                            )}
                            : <span className="text-error-700">{r.error}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="px-4 py-3 border-t border-secondary-200 flex justify-end gap-2">
          {!result ? (
            <>
              <Button variant="outline" size="sm" onClick={onDialogClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={rows.length === 0 || submitting}
                onClick={() => void onSubmit()}
              >
                {submitting
                  ? "Importing…"
                  : `Import ${rows.length} row${rows.length === 1 ? "" : "s"}`}
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={onDialogClose}>
              Done
            </Button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default PartsImportModal;
